/**
 * Shared presentation for a day of medication occurrences: grouped by local
 * time (24-hour order), each row with icon, name/dosage, note and a Dutch
 * status chip (icon + text, never color-only). Optional per-item action
 * slot keeps mutating controls out of read-only (caregiver) views.
 */
import type { ReactNode } from 'react'
import { StatusChip } from '@/components/ui'
import { ClockIcon, PillIcon } from '@/components/icons'
import { occurrenceStatusMeta } from './statuses'
import type { OccurrenceItem } from './useMedicationData'
import './medication.css'

export type MedicationGroupListProps = {
  items: OccurrenceItem[]
  /** Optional action buttons per occurrence (client-only views). */
  renderActions?: (item: OccurrenceItem) => ReactNode
}

export function occurrenceTitle(item: OccurrenceItem): string {
  if (!item.schedule) return 'Medicatiemoment'
  const quantity = item.schedule.quantity > 1 ? ` (${item.schedule.quantity}×)` : ''
  return `${item.schedule.name}${quantity}`
}

export function occurrenceSubtitle(item: OccurrenceItem): string | null {
  if (!item.schedule) return null
  return item.schedule.dosage || null
}

type TimeGroup = { localTime: string; items: OccurrenceItem[] }

function groupByTime(items: OccurrenceItem[]): TimeGroup[] {
  const groups = new Map<string, OccurrenceItem[]>()
  for (const item of items) {
    const list = groups.get(item.occurrence.localTime) ?? []
    list.push(item)
    groups.set(item.occurrence.localTime, list)
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([localTime, groupItems]) => ({ localTime, items: groupItems }))
}

export function MedicationGroupList({ items, renderActions }: MedicationGroupListProps) {
  const groups = groupByTime(items)
  return (
    <ul className="medication-groups">
      {groups.map((group) => (
        <li key={group.localTime} className="medication-group">
          <div className="medication-group-time">
            <ClockIcon size={16} />
            <span>{group.localTime}</span>
          </div>
          <ul className="medication-items">
            {group.items.map((item) => {
              const meta = occurrenceStatusMeta(item.occurrence.status)
              const actions = renderActions?.(item)
              return (
                <li key={item.occurrence.id} className="medication-item">
                  <div className="medication-item-main">
                    <span className="medication-item-icon" aria-hidden="true">
                      <PillIcon size={18} />
                    </span>
                    <span className="medication-item-text">
                      <span className="medication-item-title">{occurrenceTitle(item)}</span>
                      {occurrenceSubtitle(item) ? (
                        <span className="medication-item-subtitle">{occurrenceSubtitle(item)}</span>
                      ) : null}
                      {item.intakeEvent?.clientNote ? (
                        <span className="medication-item-note">
                          Notitie: {item.intakeEvent.clientNote}
                        </span>
                      ) : null}
                    </span>
                    <StatusChip status={meta.chip}>{meta.label}</StatusChip>
                  </div>
                  {actions ? <div className="medication-item-actions">{actions}</div> : null}
                </li>
              )
            })}
          </ul>
        </li>
      ))}
    </ul>
  )
}
